import React, { Fragment, useEffect } from "react";
import { FIRST_PHASE_ITERATIONS_PATH, STATE_KEYS } from "utils/constants/state";
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import { useFormikContext } from "formik";
import Toggle from "react-toggle";
import { getToggleProps, integerTransformer } from "utils/form";
import ZoneLink from "components/ZoneLink";
import {
  SUBSCRIPTION_SCHEDULE_COPY,
  SUBSCRIPTION_SCHEDULE_TEMPLATE,
  SUBSCRIPTION_SCHEDULE_TEMPLATE_TYPE
} from "utils/constants/subscription";
import ConnectedField from "components/ConnectedField";
import { ACTIVITY_STATE } from "utils/constants/ui";
import { useShortLinkSubscriptionSchedule } from "utils/hooks/shortLink";
import InheritenceDisplay from "components/InheritenceDisplay";
import { DOCS_ZONE_LINK } from "constants/docs";
import classnames from "classnames";

const DEFAULT_TITLE = "Installments";

/**
 * Note: Don't need to lock the installment options for links because they dont affect the overall payment amount and the subscription schedules get created after payment so there won't be any model sync issues
 * @param {Object} params
 * @param {Object} params.name - name / path of the node
 * @param {Object} params.inherited - inherited values
 * @param {Object} params.title - section title
 * @returns
 */
const Installments = (
  { name, inherited, title } = { title: DEFAULT_TITLE }
) => {
  const ctxTitle = title || DEFAULT_TITLE;
  const {
    values,
    touched,
    setFieldValue,
    setValues,
    setFieldTouched
  } = useFormikContext();

  const {
    error,
    inheritedSubscriptionSchedule,
    hasSchedule,
    disabled: disableSubscriptionSchedule,
    activeInheritence
  } = useShortLinkSubscriptionSchedule({ inherited });

  /**
   * Disable installments if schedule is present but recurring prices are not
   */
  const unsetSchedule = disableSubscriptionSchedule && hasSchedule;
  useEffect(() => {
    if (unsetSchedule) {
      setTimeout(() => {
        setFieldValue(STATE_KEYS.SHORT_LINK.SUBSCRIPTION_SCHEDULE, null);
      }, 0);
    }
  }, [unsetSchedule]);
  const iterationsPath = `${STATE_KEYS.SHORT_LINK.SUBSCRIPTION_SCHEDULE}.${FIRST_PHASE_ITERATIONS_PATH}`;

  if (activeInheritence) {
    const inheritedIterations = get(
      inheritedSubscriptionSchedule,
      FIRST_PHASE_ITERATIONS_PATH
    );
    const definedIterations = typeof inheritedIterations !== "undefined";

    return (
      <InheritenceDisplay
        title={ctxTitle}
        error={error}
        onRemove={() => {
          /**
           * Remove the inherited setting and default to 0
           * Turned "on" at 0 means we override the inherited value but in an "off" state
           */
          const initialInstallments = cloneDeep(
            SUBSCRIPTION_SCHEDULE_TEMPLATE[
              SUBSCRIPTION_SCHEDULE_TEMPLATE_TYPE.INSTALLMENTS
            ]
          );
          initialInstallments.phases[0].iterations = 0;

          setFieldValue(
            STATE_KEYS.SHORT_LINK.SUBSCRIPTION_SCHEDULE,
            initialInstallments
          );
        }}
      >
        {definedIterations ? (
          <div className="flex flex-column justify-between items-start pt3">
            <div className="f6 fw5">Payments</div>
            <div className="f6 pt2 word-break">{inheritedIterations}</div>
          </div>
        ) : (
          <div className="f6 pt2">None</div>
        )}
      </InheritenceDisplay>
    );
  } else {
    let content = null;
    if (disableSubscriptionSchedule) {
      content = (
        <div className="f6 pt2 lh-copy">
          {SUBSCRIPTION_SCHEDULE_COPY.INSTALLMENTS.DISABLED}
        </div>
      );
    } else {
      content = (
        <Fragment>
          <div className="flex flex-column w-100">
            <div className="flex flex-column flex-row-ns justify-between-ns items-center pt3">
              <div className="flex flex-column f6 lh-copy">
                {SUBSCRIPTION_SCHEDULE_COPY.INSTALLMENTS.ITERATIONS_LABEL}
              </div>
              <div className="w-100 w-50-ns flex flex-row items-center justify-between justify-end-ns pt3 pt0-ns">
                <div className="flex flex-row items-center justify-end">
                  <div className="w3">
                    <ConnectedField
                      key={`iteration-${
                        hasSchedule
                          ? ACTIVITY_STATE.ENABLED
                          : ACTIVITY_STATE.DISABLED
                      }`}
                      disabled={!hasSchedule}
                      name={iterationsPath}
                      transformer={integerTransformer}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="f6 pt2 lh-copy">
              {SUBSCRIPTION_SCHEDULE_COPY.INSTALLMENTS.ENABLED}
            </div>
          </div>
          <div className="flex flex-column pt2">
            <div className="f6 lh-copy word-break">
              {SUBSCRIPTION_SCHEDULE_COPY.INSTALLMENTS.FOOTER}
            </div>
            <div className="pt2">
              <ZoneLink
                href={DOCS_ZONE_LINK.LEARN_PAYMENT_LINKS_DETAILS_RESTRICTIONS}
                zone
                linkProps={{
                  className: "link",
                  target: "_blank"
                }}
              >
                <div className="f6 fw6 link__decoration--hover color-primary pointer">
                  Learn more
                </div>
              </ZoneLink>
            </div>
          </div>
        </Fragment>
      );
    }

    return (
      <div className="flex flex-column w-100">
        <div className="flex flex-row items-center justify-between">
          <label
            className={classnames("f5 fw6 db", {
              "color-error": error
            })}
          >
            Installments
          </label>
          <Toggle
            {...getToggleProps({
              active: hasSchedule,
              disabled: disableSubscriptionSchedule,
              setFieldValue: (_, active) => {
                if (
                  !get(touched, STATE_KEYS.SHORT_LINK.SUBSCRIPTION_SCHEDULE)
                ) {
                  setFieldTouched(
                    STATE_KEYS.SHORT_LINK.SUBSCRIPTION_SCHEDULE,
                    true
                  );
                }

                setTimeout(() => {
                  if (active) {
                    setFieldValue(
                      STATE_KEYS.SHORT_LINK.SUBSCRIPTION_SCHEDULE,
                      cloneDeep(
                        SUBSCRIPTION_SCHEDULE_TEMPLATE[
                          SUBSCRIPTION_SCHEDULE_TEMPLATE_TYPE.INSTALLMENTS
                        ]
                      )
                    );
                  } else {
                    const updatedValues = cloneDeep(values);
                    delete updatedValues.data.product.subscription_schedule;
                    setValues(updatedValues);
                  }
                }, 0);
              }
            })}
          />
        </div>
        {content}
      </div>
    );
  }
};

export default Installments;
